import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { TabProvider, useTabManager } from '../../contexts/TabContext';
import { TabBar } from '../navigation/TabBar';
import { ProjectDetailsPage } from '../../../features/projects/pages/ProjectDetailsPage';
import { JobDetails } from '../../../features/jobs/components/JobDetails';
import { OdysseyJobDetails } from '../../../features/jobs/components/OdysseyJobDetails';
import { EntryDetailPage } from '../../../features/notebook/pages/EntryDetailPage';
import { DataEntryDetailPage } from '../../../features/notebook/pages/DataEntryDetailPage';
import { MoleculeDetailPage } from '../../../features/molecules/pages/MoleculeDetailPage';
import { UnifiedChat } from '../../../features/chat/components/UnifiedChat';
import { JobsPage } from '../../../features/jobs/pages/JobsPage';
import { EntriesPage } from '../../../features/notebook/pages/EntriesPage';
import { DataEntriesPage } from '../../../features/notebook/pages/DataEntriesPage';
import { ProjectSettingsPage } from '../../../features/projects/pages/ProjectSettingsPage';
import { Sidebar } from '../navigation/Sidebar';
import { DataTablesSidebar } from '../../../features/notebook/components/DataTablesSidebar';
import { EntriesSidebar } from '../../../features/notebook/components/EntriesSidebar';
import { GenerationsSidebar } from '../../../features/jobs/components/GenerationsSidebar';
import { FilePreparerSidebar } from '../../../features/notebook/components/FilePreparerSidebar';
import { Tab } from '../../types/tabs';
import { getProject } from '../../../features/projects/lib/projects';
import type { Project } from '../../../shared/lib/supabase';
import { Home, Briefcase, BookOpen, Table, MessageSquare, ChevronLeft, ChevronRight, Settings, Grid3x3, Zap, FileCode, Dna, Rocket } from 'lucide-react';
import { toast } from 'sonner';

// Error boundary for individual tab content
class TabErrorBoundary extends React.Component<
  { children: React.ReactNode; tabId: string; tabType: string },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode; tabId: string; tabType: string }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`Tab content error (${this.props.tabType}/${this.props.tabId}):`, error);
    console.error('Error info:', errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 text-center">
          <div className="bg-red-50 border border-red-200 rounded-sm p-4 max-w-md mx-auto">
            <h3 className="text-red-800 font-medium mb-2">Tab Content Error</h3>
            <p className="text-red-600 text-sm mb-3">
              This tab encountered an error and couldn't be displayed.
            </p>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              className="px-3 py-1 bg-red-600 text-white text-sm rounded-sm hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Tab content renderer component
function TabContent({ tab, project, onTabOpen, onOpenMolecule }: { 
  tab: Tab; 
  project: Project; 
  onTabOpen: (type: string) => void;
  onOpenMolecule?: (moleculeId: string, name: string) => void;
}) {
  switch (tab.type) {
    case 'home':
      return <ProjectDetailsPage onOpenChat={() => onTabOpen('chat')} />;
    
    case 'generation':
      // Show individual job details
      if (tab.data?.jobId) {
        return <JobDetails jobId={tab.data.jobId} />;
      }
      return <div className="p-6 text-center text-gray-500">No job selected</div>;
    
    case 'odyssey':
      // Show Odyssey job details
      if (tab.data?.odysseyJobId) {
        return <OdysseyJobDetails odysseyJobId={tab.data.odysseyJobId} lazyLoad={false} />;
      }
      return <div className="p-6 text-center text-gray-500">No Odyssey job selected</div>;
    
    case 'entry':
      // Show individual entry details
      if (tab.data?.entryId) {
        return <EntryDetailPage entryId={tab.data.entryId} />;
      }
      return <div className="p-6 text-center text-gray-500">No entry selected</div>;
    
    case 'table':
      // Show individual table details
      if (tab.data?.dataEntryId) {
        return <DataEntryDetailPage dataEntryId={tab.data.dataEntryId} />;
      }
      return <div className="p-6 text-center text-gray-500">No table selected</div>;
    
    case 'chat':
      return <UnifiedChat project={project} />;
    
    case 'settings':
      return <ProjectSettingsPage />;
    
    case 'molecule':
      // Show individual molecule details
      if (tab.data?.moleculeId) {
        return <MoleculeDetailPage 
          moleculeId={tab.data.moleculeId} 
          runName={tab.data?.runName} 
          tabId={tab.id}
          maskData={tab.data?.maskData}
        />;
      }
      return <div className="p-6 text-center text-gray-500">No molecule selected</div>;
    
    default:
      return <div>Unknown tab type</div>;
  }
}

// Inner component that uses the tab context
function ProjectTabLayoutInner() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { tabs, activeTab, openTab, closeTab, switchTab, updateTab, filterTabsByProject } = useTabManager();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [homeTabCreated, setHomeTabCreated] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showTablesSidebar, setShowTablesSidebar] = useState(false);
  const [showEntriesSidebar, setShowEntriesSidebar] = useState(false);
  const [showGenerationsSidebar, setShowGenerationsSidebar] = useState(false);
  const [showFilePreparerSidebar, setShowFilePreparerSidebar] = useState(false);
  const [lastInteraction, setLastInteraction] = useState<{ type: 'tab' | 'sidebar'; label?: string } | undefined>(undefined);
  // Ensure a clean slate on hard refresh: start on Home and don't auto-open tabs from URL
  const firstLoadRef = useRef(true);
  
  // console.log('ProjectTabLayoutInner - Current path:', location.pathname);
  // console.log('ProjectTabLayoutInner - Project ID:', projectId);
  
  // Track recently closed tabs to prevent immediate recreation
  const recentlyClosedTabs = useRef<Set<string>>(new Set());
  
  // Track recently created tabs to prevent duplicate creation from URL navigation
  const recentlyCreatedTabs = useRef<Set<string>>(new Set());
  
  // Track if useEffect is currently executing to prevent race conditions
  const isExecutingRef = useRef<boolean>(false);
  
  // Check if we're on a list page (these should show the list page content, not tabs)
  const pathSegments = location.pathname.split('/');
  const lastSegment = pathSegments[pathSegments.length - 1];
  const isOnListPage = lastSegment === 'jobs' || 
                      lastSegment === 'entries' || 
                      lastSegment === 'data-entries' ||
                      lastSegment === 'settings';
  
  // Helper function to create a unique key for tab identification
  const createTabKey = (type: string, dataId: string) => `${type}:${dataId}`;

  // Load project details and filter tabs when project changes
  useEffect(() => {
    if (projectId) {
      // Reset project state to prevent stale data
      setProject(null);
      setHomeTabCreated(false);
      
      // Store current project ID in session storage
      sessionStorage.setItem('currentProjectId', projectId);
      
      // Filter tabs to only show those from the current project
      if (filterTabsByProject) {
        filterTabsByProject(projectId);
      }
      
      loadProject();
    }
  }, [projectId, filterTabsByProject]);

  // Create home tab and chat tab only once when project is loaded
  useEffect(() => {
    if (project && !homeTabCreated && projectId) {
      const existingHomeTab = tabs.find(tab => tab.type === 'home' && tab.projectId === projectId);
      const existingChatTab = tabs.find(tab => tab.type === 'chat' && tab.projectId === projectId);
      
      // Create or update home tab
      if (!existingHomeTab) {
        console.log('Creating home tab for project:', project.name);
        const newHomeTab = {
          type: 'home' as const,
          title: project.name,
          icon: Home,
          closable: false,
          projectId
        };
        openTab(newHomeTab);
      } else if (existingHomeTab.title !== project.name) {
        // Update the title if it doesn't match the current project name
        console.log('Updating home tab title for project:', project.name);
        updateTab(existingHomeTab.id, { title: project.name });
      }
      
      
      // Ensure home tab is active when project first loads
      // Only switch if we're not already on the correct project
      if (!activeTab || activeTab.projectId !== projectId) {
        const homeTab = tabs.find(tab => tab.type === 'home' && tab.projectId === projectId);
        if (homeTab) {
          if (import.meta.env.DEV) {
            console.log('Ensuring home tab is active on project load');
          }
          switchTab(homeTab.id);
        }
      }
      
      setHomeTabCreated(true);
    }
  }, [project, projectId, tabs, openTab, switchTab, updateTab, homeTabCreated, activeTab]);


  // Listen for switchToHomeTab event
  useEffect(() => {
    const handleSwitchToHomeTab = (event: CustomEvent) => {
      if (event.detail?.projectId === projectId) {
        const homeTab = tabs.find(tab => tab.type === 'home' && tab.projectId === projectId);
        if (homeTab) {
          switchTab(homeTab.id);
        }
      }
    };

    window.addEventListener('switchToHomeTab', handleSwitchToHomeTab as any);
    return () => {
      window.removeEventListener('switchToHomeTab', handleSwitchToHomeTab as any);
    };
  }, [tabs, projectId, switchTab]);

  // When navigating to a specific item (job, entry, table), open the appropriate tab
  useEffect(() => {
    if (!project || !projectId) return;
    
    // Prevent race conditions by checking if already executing
    if (isExecutingRef.current) {
      console.log('🚫 Skipping useEffect execution - already in progress');
      return;
    }

    // Use requestAnimationFrame for better performance than setTimeout
    const frameId = requestAnimationFrame(() => {
      isExecutingRef.current = true;
      // On the very first load of this layout, ignore deep URLs and force Home
      if (firstLoadRef.current) {
        firstLoadRef.current = false;
        const homeTab = tabs.find(tab => tab.type === 'home' && tab.projectId === projectId);
        if (homeTab) {
          switchTab(homeTab.id);
        }
        navigate(`/projects/${projectId}`);
        isExecutingRef.current = false;
        return;
      }
      const pathSegments = location.pathname.split('/');
      const lastSegment = pathSegments[pathSegments.length - 1];
      
      // Check if we're on a specific item page (not a list page)
      if (pathSegments.includes('jobs') && lastSegment !== 'jobs' && lastSegment !== projectId) {
        // We're on a specific job page
        const jobId = lastSegment;
        const tabKey = createTabKey('generation', jobId);
        
        // Check if this tab was recently closed or created
        if (recentlyClosedTabs.current.has(tabKey)) {
          console.log('🚫 Skipping tab recreation for recently closed job:', jobId);
          return;
        }
        
        if (recentlyCreatedTabs.current.has(tabKey)) {
          console.log('⏭️ Skipping tab creation - already created programmatically:', jobId);
          return;
        }
        
        const existingTab = tabs.find(tab => tab.type === 'generation' && tab.data?.jobId === jobId);
        
        if (!existingTab) {
          console.log('📝 Creating generation tab for job:', jobId);
          // Create a new generation tab
          openTab({
            type: 'generation',
            title: 'Loading...',
            icon: Briefcase,
            closable: true,
            projectId,
            loading: true,
            data: { jobId }
          });
          
          // Mark this tab as recently created and clear after a delay
          recentlyCreatedTabs.current.add(tabKey);
          setTimeout(() => {
            recentlyCreatedTabs.current.delete(tabKey);
          }, 500);
        } else {
          // Switch to existing tab
          switchTab(existingTab.id);
        }
    } else if (pathSegments.includes('entries') && lastSegment !== 'entries' && lastSegment !== projectId) {
      // We're on a specific entry page
      const entryId = lastSegment;
      const tabKey = createTabKey('entry', entryId);
      
      // Check if this tab was recently closed
      if (recentlyClosedTabs.current.has(tabKey)) {
        console.log('🚫 Skipping tab recreation for recently closed entry:', entryId);
        return;
      }
      
      if (recentlyCreatedTabs.current.has(tabKey)) {
        console.log('⏭️ Skipping tab creation - already created programmatically:', entryId);
        return;
      }
      
      const existingTab = tabs.find(tab => tab.type === 'entry' && tab.data?.entryId === entryId);
      
      if (!existingTab) {
        console.log('📝 Creating entry tab for entry:', entryId);
        // Create a new entry tab
        openTab({
          type: 'entry',
          title: 'Loading...',
          icon: BookOpen,
          closable: true,
          projectId,
          loading: true,
          data: { entryId }
        });
        
        // Mark this tab as recently created and clear after a delay
        recentlyCreatedTabs.current.add(tabKey);
        setTimeout(() => {
          recentlyCreatedTabs.current.delete(tabKey);
        }, 500);
      } else {
        // Switch to existing tab
        switchTab(existingTab.id);
      }
    } else if (pathSegments.includes('data-entries') && lastSegment !== 'data-entries' && lastSegment !== projectId) {
      // We're on a specific data entry page
      const dataEntryId = lastSegment;
      const tabKey = createTabKey('table', dataEntryId);
      
      // Check if this tab was recently closed
      if (recentlyClosedTabs.current.has(tabKey)) {
        console.log('🚫 Skipping tab recreation for recently closed table:', dataEntryId);
        return;
      }
      
      if (recentlyCreatedTabs.current.has(tabKey)) {
        console.log('⏭️ Skipping tab creation - already created programmatically:', dataEntryId);
        return;
      }
      
      const existingTab = tabs.find(tab => tab.type === 'table' && tab.data?.dataEntryId === dataEntryId);
      
      if (!existingTab) {
        console.log('📝 Creating table tab for dataEntry:', dataEntryId);
        // Create a new table tab
        openTab({
          type: 'table',
          title: 'Loading...',
          icon: Table,
          closable: true,
          projectId,
          loading: true,
          data: { dataEntryId }
        });
        
        // Mark this tab as recently created and clear after a delay
        recentlyCreatedTabs.current.add(tabKey);
        setTimeout(() => {
          recentlyCreatedTabs.current.delete(tabKey);
        }, 500);
      } else {
        // Switch to existing tab
        switchTab(existingTab.id);
      }
    } else if (pathSegments.includes('odyssey') && lastSegment !== 'odyssey' && lastSegment !== projectId) {
      // We're on a specific Odyssey job page
      const odysseyJobId = lastSegment;
      const tabKey = createTabKey('odyssey', odysseyJobId);
      
      // Check if this tab was recently closed
      if (recentlyClosedTabs.current.has(tabKey)) {
        console.log('🚫 Skipping tab recreation for recently closed Odyssey job:', odysseyJobId);
        return;
      }
      
      // First check if tab already exists (this handles tabs created by useTabNavigation)
      const existingTab = tabs.find(tab => tab.type === 'odyssey' && tab.data?.odysseyJobId === odysseyJobId);
      
      if (existingTab) {
        // Tab already exists, just switch to it and mark as recently created to prevent future duplicates
        console.log('🔄 Switching to existing Odyssey tab:', odysseyJobId);
        switchTab(existingTab.id);
        recentlyCreatedTabs.current.add(tabKey);
        return;
      }
      
      if (recentlyCreatedTabs.current.has(tabKey)) {
        console.log('⏭️ Skipping tab creation - already created programmatically:', odysseyJobId);
        return;
      }
      
      console.log('📝 Creating Odyssey tab for job:', odysseyJobId);
      // Create a new Odyssey tab
      openTab({
          type: 'odyssey',
          title: 'Loading...',
          icon: Dna,
          closable: true,
          projectId,
          loading: true,
          data: { odysseyJobId }
        });
        
        // Mark this tab as recently created and clear after a delay
        recentlyCreatedTabs.current.add(tabKey);
        setTimeout(() => {
          recentlyCreatedTabs.current.delete(tabKey);
        }, 500);
    } else if (pathSegments.includes('molecules') && lastSegment !== 'molecules' && lastSegment !== projectId) {
      // We're on a specific molecule page
      const moleculeId = lastSegment;
      const tabKey = createTabKey('molecule', moleculeId);
      
      console.log('🧪 ProjectTabLayout detected molecule URL:', moleculeId);
      
      // Check if this tab was recently closed
      if (recentlyClosedTabs.current.has(tabKey)) {
        console.log('🚫 Skipping tab recreation for recently closed molecule:', moleculeId);
        return;
      }
      
      if (recentlyCreatedTabs.current.has(tabKey)) {
        console.log('⏭️ Skipping tab creation - already created programmatically:', moleculeId);
        return;
      }
      
      // First check if tab already exists (this handles tabs created by useTabNavigation)
      const existingTab = tabs.find(tab => tab.type === 'molecule' && tab.data?.moleculeId === moleculeId);
      
      if (existingTab) {
        // Tab already exists, just switch to it and mark as recently created to prevent future duplicates
        console.log('🔄 Switching to existing molecule tab:', moleculeId);
        switchTab(existingTab.id);
        recentlyCreatedTabs.current.add(tabKey);
        return;
      }
      
      if (!existingTab) {
        console.log('📝 Creating molecule tab for molecule:', moleculeId);
        // Create a new molecule tab
        openTab({
          type: 'molecule',
          title: 'Loading...',
          icon: FileCode,
          closable: true,
          projectId,
          loading: true,
          data: { moleculeId }
        });
        
        // Mark this tab as recently created and clear after a delay
        recentlyCreatedTabs.current.add(tabKey);
        setTimeout(() => {
          recentlyCreatedTabs.current.delete(tabKey);
        }, 500);
      } else {
        // Switch to existing tab
        switchTab(existingTab.id);
      }
    } else if (lastSegment === 'settings') {
      // We're on the settings page
      const tabKey = createTabKey('settings', projectId);
      
      // Check if this tab was recently closed
      if (recentlyClosedTabs.current.has(tabKey)) {
        console.log('🚫 Skipping tab recreation for recently closed settings');
        return;
      }
      
      const existingTab = tabs.find(tab => tab.type === 'settings' && tab.projectId === projectId);
      
      if (!existingTab) {
        console.log('📝 Creating settings tab');
        // Create a new settings tab
        openTab({
          type: 'settings',
          title: 'Project Settings',
          icon: Settings,
          closable: true,
          projectId
        });
      } else {
        // Switch to existing tab
        switchTab(existingTab.id);
      }
    } else if (lastSegment === 'filepreparer') {
      // We're on the file preparer page
      const tabKey = createTabKey('filepreparer', projectId);
      
      // Check if this tab was recently closed
      if (recentlyClosedTabs.current.has(tabKey)) {
        console.log('🚫 Skipping tab recreation for recently closed file preparer');
        return;
      }
      
      const existingTab = tabs.find(tab => tab.type === 'filepreparer' && tab.projectId === projectId);
      
      if (!existingTab) {
        console.log('📝 Creating file preparer tab');
        // Create a new file preparer tab
        openTab({
          type: 'filepreparer',
          title: 'Protein Design',
          icon: FileCode,
          closable: true,
          projectId
        });
      } else {
        // Switch to existing tab
        switchTab(existingTab.id);
      }
    }
      
      // Reset execution guard
      isExecutingRef.current = false;
    }); // Use requestAnimationFrame instead of timeout
    
    return () => {
      cancelAnimationFrame(frameId);
      isExecutingRef.current = false;
    };
  }, [location.pathname, project, projectId]);

  // Effect to handle navigation when active tab changes (e.g., from closing a tab)
  useEffect(() => {
    if (!activeTab || !projectId) return;
    
    // Don't sync navigation when on a list page - list pages are independent of tabs
    if (isOnListPage) return;
    
    // Get current path
    const currentPath = location.pathname;
    
    // Determine what the path should be based on the active tab
    let expectedPath = '';
    switch (activeTab.type) {
      case 'home':
        expectedPath = `/projects/${projectId}`;
        break;
      case 'generation':
        if (activeTab.data?.jobId) {
          expectedPath = `/projects/${projectId}/jobs/${activeTab.data.jobId}`;
        }
        break;
      case 'entry':
        if (activeTab.data?.entryId) {
          expectedPath = `/projects/${projectId}/entries/${activeTab.data.entryId}`;
        }
        break;
      case 'table':
        if (activeTab.data?.dataEntryId) {
          expectedPath = `/projects/${projectId}/data-entries/${activeTab.data.dataEntryId}`;
        }
        break;
      case 'settings':
        expectedPath = `/projects/${projectId}/settings`;
        break;
      case 'filepreparer':
        expectedPath = `/projects/${projectId}/filepreparer`;
        break;
      case 'molecule':
        if (activeTab.data?.moleculeId) {
          expectedPath = `/projects/${projectId}/molecules/${activeTab.data.moleculeId}`;
        }
        break;
      case 'odyssey':
        if (activeTab.data?.odysseyJobId) {
          expectedPath = `/projects/${projectId}/odyssey/${activeTab.data.odysseyJobId}`;
        }
        break;
    }
    
    // Navigate if the current path doesn't match the expected path
    if (expectedPath && currentPath !== expectedPath) {
      navigate(expectedPath);
    }
  }, [activeTab, projectId, location.pathname, navigate, isOnListPage]);

  // Handle tab switching - this will update the URL appropriately
  const handleTabSwitch = (tabId: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;
    
    switchTab(tabId);
    
    // Mark this as a tab interaction
    setLastInteraction({ type: 'tab' });
    
    // Update URL based on tab type
    switch (tab.type) {
      case 'home':
        navigate(`/projects/${projectId}`);
        break;
      case 'generation':
        if (tab.data?.jobId) {
          navigate(`/projects/${projectId}/jobs/${tab.data.jobId}`);
        }
        break;
      case 'entry':
        if (tab.data?.entryId) {
          navigate(`/projects/${projectId}/entries/${tab.data.entryId}`);
        }
        break;
      case 'table':
        if (tab.data?.dataEntryId) {
          navigate(`/projects/${projectId}/data-entries/${tab.data.dataEntryId}`);
        }
        break;
      case 'chat':
        // If we're on a list page, navigate to the project page to show the chat
        if (isOnListPage) {
          navigate(`/projects/${projectId}`);
        }
        // Otherwise stay on current URL and show chat content
        break;
      case 'settings':
        navigate(`/projects/${projectId}/settings`);
        break;
      case 'molecule':
        if (tab.data?.moleculeId) {
          navigate(`/projects/${projectId}/molecules/${tab.data.moleculeId}`);
        }
        break;
      case 'odyssey':
        if (tab.data?.odysseyJobId) {
          navigate(`/projects/${projectId}/odyssey/${tab.data.odysseyJobId}`);
        }
        break;
    }
  };

  // Enhanced close tab handler that prevents immediate recreation
  const handleCloseTab = async (tabId: string) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab || !projectId) return;
    
    // Add to recently closed tabs to prevent recreation
    let tabKey = '';
    switch (tab.type) {
      case 'generation':
        if (tab.data?.jobId) {
          tabKey = createTabKey('generation', tab.data.jobId);
        }
        break;
      case 'entry':
        if (tab.data?.entryId) {
          tabKey = createTabKey('entry', tab.data.entryId);
        }
        break;
      case 'table':
        if (tab.data?.dataEntryId) {
          tabKey = createTabKey('table', tab.data.dataEntryId);
        }
        break;
      case 'settings':
        tabKey = createTabKey('settings', projectId);
        break;
      case 'filepreparer':
        tabKey = createTabKey('filepreparer', projectId);
        break;
      case 'molecule':
        if (tab.data?.moleculeId) {
          tabKey = createTabKey('molecule', tab.data.moleculeId);
        }
        break;
      case 'odyssey':
        if (tab.data?.odysseyJobId) {
          tabKey = createTabKey('odyssey', tab.data.odysseyJobId);
        }
        break;
    }
    
    if (tabKey) {
      recentlyClosedTabs.current.add(tabKey);
      console.log('🔒 Added to recently closed tabs:', tabKey);
      
      // Remove from recently closed after 2 seconds
      setTimeout(() => {
        recentlyClosedTabs.current.delete(tabKey);
        console.log('🔓 Removed from recently closed tabs:', tabKey);
      }, 2000);
    }
    
    // If we're closing the settings tab and currently on the settings page, navigate to home
    if (tab.type === 'settings' && location.pathname.endsWith('/settings')) {
      navigate(`/projects/${projectId}`);
    }
    
    // If we're closing the filepreparer tab and currently on the filepreparer page, navigate to home
    if (tab.type === 'filepreparer' && location.pathname.endsWith('/filepreparer')) {
      navigate(`/projects/${projectId}`);
    }
    
    // Close the tab
    closeTab(tabId);
    
    // Reset last interaction to tab type since we're switching tabs after close
    setLastInteraction({ type: 'tab' });
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle shortcuts when not in an input field
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Ctrl/Cmd + Tab: Switch to next tab
      if ((e.ctrlKey || e.metaKey) && e.key === 'Tab') {
        e.preventDefault();
        const currentIndex = tabs.findIndex(tab => tab.id === activeTab?.id);
        if (currentIndex !== -1 && tabs.length > 1) {
          const nextIndex = e.shiftKey 
            ? (currentIndex - 1 + tabs.length) % tabs.length 
            : (currentIndex + 1) % tabs.length;
          handleTabSwitch(tabs[nextIndex].id);
        }
      }
      
      // Ctrl/Cmd + W: Close current tab
      if ((e.ctrlKey || e.metaKey) && e.key === 'w') {
        e.preventDefault();
        if (activeTab && activeTab.closable) {
          handleCloseTab(activeTab.id);
        }
      }
      
      // Ctrl/Cmd + 1-9: Switch to specific tab
      if ((e.ctrlKey || e.metaKey) && e.key >= '1' && e.key <= '9') {
        e.preventDefault();
        const tabIndex = parseInt(e.key) - 1;
        if (tabIndex < tabs.length) {
          handleTabSwitch(tabs[tabIndex].id);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [tabs, activeTab, handleTabSwitch]);

  const loadProject = async () => {
    if (!projectId) return;
    
    try {
      const projectData = await getProject(projectId);
      setProject(projectData);
    } catch (error) {
      console.error('Error loading project:', error);
      toast.error('Failed to load project');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleSidebarItemClick = (label: string) => {
    // Mark this as a sidebar interaction
    setLastInteraction({ type: 'sidebar', label });
  };

  const handleToggleTablesSidebar = () => {
    setShowTablesSidebar(prev => !prev);
    setShowEntriesSidebar(false);
    setShowGenerationsSidebar(false);
    setShowFilePreparerSidebar(false);
  };

  const handleToggleEntriesSidebar = () => {
    setShowEntriesSidebar(prev => !prev);
    setShowTablesSidebar(false);
    setShowGenerationsSidebar(false);
    setShowFilePreparerSidebar(false);
  };

  const handleToggleGenerationsSidebar = () => {
    setShowGenerationsSidebar(prev => !prev);
    setShowTablesSidebar(false);
    setShowEntriesSidebar(false);
    setShowFilePreparerSidebar(false);
  };

  const handleToggleFilePreparerSidebar = () => {
    setShowFilePreparerSidebar(prev => !prev);
    setShowTablesSidebar(false);
    setShowEntriesSidebar(false);
    setShowGenerationsSidebar(false);
  };

  const handleOpenDataTable = (dataEntryId: string, name: string) => {
    if (!projectId) return;
    
    // Check if this table tab already exists
    const existingTab = tabs.find(tab => 
      tab.type === 'table' && tab.data?.dataEntryId === dataEntryId
    );
    
    if (existingTab) {
      // Switch to existing tab
      switchTab(existingTab.id);
    } else {
      // Create new tab for this data table
      openTab({
        type: 'table',
        title: name,
        icon: Grid3x3,
        closable: true,
        projectId,
        data: { dataEntryId }
      });
    }
  };

  const handleOpenEntry = (entryId: string, name: string) => {
    if (!projectId) return;
    
    // Check if this entry tab already exists
    const existingTab = tabs.find(tab => 
      tab.type === 'entry' && tab.data?.entryId === entryId
    );
    
    if (existingTab) {
      // Switch to existing tab
      switchTab(existingTab.id);
    } else {
      // Create new tab for this entry
      openTab({
        type: 'entry',
        title: name,
        icon: BookOpen,
        closable: true,
        projectId,
        data: { entryId }
      });
    }
  };

  const handleOpenGeneration = (jobId: string, name: string) => {
    if (!projectId) return;
    
    // Check if this Odyssey tab already exists
    const existingTab = tabs.find(tab => 
      tab.type === 'odyssey' && tab.data?.odysseyJobId === jobId
    );
    
    if (existingTab) {
      // Switch to existing tab
      switchTab(existingTab.id);
    } else {
      // Create new tab for this Odyssey job
      openTab({
        type: 'odyssey',
        title: name,
        icon: Rocket,
        closable: true,
        projectId,
        data: { odysseyJobId: jobId }
      });
    }
  };

  const handleOpenMolecule = (moleculeId: string, name: string) => {
    if (!projectId) return;
    
    // Check if this molecule tab already exists
    const existingTab = tabs.find(tab => 
      tab.type === 'molecule' && tab.data?.moleculeId === moleculeId
    );
    
    if (existingTab) {
      // Switch to existing tab
      switchTab(existingTab.id);
    } else {
      // Create new tab for this molecule
      openTab({
        type: 'molecule',
        title: name,
        icon: FileCode,
        closable: true,
        projectId,
        data: { moleculeId }
      });
    }
  };

  const handleTabOpen = (type: string) => {
    if (!projectId) return;
    
    switch (type) {
      case 'home':
        // Check if home tab already exists before creating
        const existingHomeTab = tabs.find(tab => tab.type === 'home' && tab.projectId === projectId);
        if (existingHomeTab) {
          handleTabSwitch(existingHomeTab.id);
        } else {
          openTab({
            type: 'home',
            title: project?.name || 'Home',
            icon: Home,
            closable: false,
            projectId
          });
          // Navigate to home after creating tab
          navigate(`/projects/${projectId}`);
        }
        break;
      
      case 'chat':
        // OLD PIPELINE DISABLED - Comment out chat tab creation to prevent access to old AWS pipeline
        // Users should only use the Odyssey JSON Creator workflow now
        
        // // Check if chat tab already exists before creating
        // const existingChatTab = tabs.find(tab => tab.type === 'chat' && tab.projectId === projectId);
        // if (existingChatTab) {
        //   handleTabSwitch(existingChatTab.id);
        // } else {
        //   // Get the appropriate title based on workflow type
        //   let chatTabTitle = 'AI Chatbot';
        //   if (project) {
        //     switch (project.workflow_type) {
        //       case 'enzyme':
        //         chatTabTitle = 'Enzyme Design';
        //         break;
        //       case 'antibody':
        //         chatTabTitle = 'Antibody Design';
        //         break;
        //       case 'peptide':
        //         chatTabTitle = 'Peptide Design';
        //         break;
        //     }
        //   }
        //   
        //   openTab({
        //     type: 'chat',
        //     title: chatTabTitle,
        //     icon: MessageSquare,
        //     closable: true,
        //     projectId
        //   });
        // }
        
        console.log('Old workflow chat interface disabled - please use Protein Design (JSON Creator) instead');
        break;
        
      case 'settings':
        // Check if settings tab already exists before creating
        const existingSettingsTab = tabs.find(tab => tab.type === 'settings' && tab.projectId === projectId);
        if (existingSettingsTab) {
          handleTabSwitch(existingSettingsTab.id);
        } else {
          openTab({
            type: 'settings',
            title: 'Project Settings',
            icon: Settings,
            closable: true,  // Make it closable unlike home and chat
            projectId
          });
          // Navigate to settings after creating tab
          navigate(`/projects/${projectId}/settings`);
        }
        break;
        
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading project...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return <div>Project not found</div>;
  }

  return (
    <div className="flex h-screen">
      {/* Sidebar - Project-specific sidebar */}
      <div className={`${sidebarCollapsed ? 'w-16 sidebar-width-collapsed' : 'w-56 sidebar-width-expanded'} bg-white border-r border-gray-200 transition-all duration-300 sidebar-transition flex flex-col relative`}>
        {/* Only render ONE sidebar, and it's project-specific */}
        <Sidebar 
          collapsed={sidebarCollapsed} 
          useTabNavigation={true} 
          onTabOpen={handleTabOpen}
          onCollapsedChange={setSidebarCollapsed}
          onToggleTablesSidebar={handleToggleTablesSidebar}
          showTablesSidebar={showTablesSidebar}
          onToggleEntriesSidebar={handleToggleEntriesSidebar}
          showEntriesSidebar={showEntriesSidebar}
          onToggleGenerationsSidebar={handleToggleGenerationsSidebar}
          showGenerationsSidebar={showGenerationsSidebar}
          onToggleFilePreparerSidebar={handleToggleFilePreparerSidebar}
          showFilePreparerSidebar={showFilePreparerSidebar}
          activeTabType={activeTab?.type}
          lastInteraction={lastInteraction}
          onSidebarItemClick={handleSidebarItemClick}
        />
      </div>

      {/* Secondary sidebars - only one shows at a time */}
      {showTablesSidebar && projectId && (
        <div className="h-full pt-16 bg-white">
          <DataTablesSidebar
            projectId={projectId}
            onOpenTable={handleOpenDataTable}
            onClose={handleToggleTablesSidebar}
          />
        </div>
      )}
      
      {showEntriesSidebar && projectId && (
        <div className="h-full pt-16 bg-white">
          <EntriesSidebar
            projectId={projectId}
            onOpenEntry={handleOpenEntry}
            onClose={handleToggleEntriesSidebar}
          />
        </div>
      )}
      
      {showGenerationsSidebar && projectId && (
        <div className="h-full pt-16 bg-white">
          <GenerationsSidebar
            projectId={projectId}
            onOpenGeneration={handleOpenGeneration}
            onClose={handleToggleGenerationsSidebar}
            onOpenWorkflow={() => {
              // Close the iterations sidebar
              setShowGenerationsSidebar(false);
              // Open the chat tab (which shows the workflow)
              handleTabOpen('chat');
            }}
          />
        </div>
      )}
      
      {/* FilePreparerSidebar - Always mounted but conditionally visible */}
      <div className={`h-full pt-16 bg-white ${showFilePreparerSidebar ? 'block' : 'hidden'}`}>
        <FilePreparerSidebar
          projectId={projectId || ''} // Always provide a projectId, even if empty
          onOpenMolecule={handleOpenMolecule}
          onClose={handleToggleFilePreparerSidebar}
          isVisible={showFilePreparerSidebar && !!projectId} // Only visible if both conditions are true
        />
      </div>

      {/* Centered vertical divider between secondary sidebars and tabs */}
      {(showTablesSidebar || showEntriesSidebar || showGenerationsSidebar || showFilePreparerSidebar) && (
        <div className="w-px bg-gray-200 h-full pt-16 flex-shrink-0"></div>
      )}

      {/* Main content area with tabs */}
      <div className="flex-1 relative bg-gray-100 overflow-hidden">
        
        {/* Tab bar with custom close handler - positioned absolutely */}
        <div className="absolute top-0 left-0 right-0 z-10 bg-gray-100 pt-16">
          <TabBar onTabSwitch={handleTabSwitch} onCloseTab={handleCloseTab} />
        </div>
        
        {/* Tab content or list pages - positioned absolutely with proper top constraint */}
        <main className="absolute left-0 right-0 bottom-0 overflow-auto bg-white" style={{ top: '104px' }}>
          {activeTab && activeTab.type === 'chat' && !isOnListPage ? (
            // Show chat tab content when chat tab is active AND we're not on a list page
            <TabErrorBoundary tabId={activeTab.id} tabType={activeTab.type}>
              <TabContent tab={activeTab} project={project} onTabOpen={handleTabOpen} onOpenMolecule={handleOpenMolecule} />
            </TabErrorBoundary>
          ) : isOnListPage ? (
            // Show list pages when on their URLs - wrap each in error boundary
            <>
              {lastSegment === 'jobs' && (
                <TabErrorBoundary tabId="jobs-page" tabType="jobs-list">
                  <JobsPage />
                </TabErrorBoundary>
              )}
              {lastSegment === 'entries' && (
                <TabErrorBoundary tabId="entries-page" tabType="entries-list">
                  <EntriesPage />
                </TabErrorBoundary>
              )}
              {lastSegment === 'data-entries' && (
                <TabErrorBoundary tabId="data-entries-page" tabType="data-entries-list">
                  <DataEntriesPage />
                </TabErrorBoundary>
              )}
              {lastSegment === 'settings' && (
                <TabErrorBoundary tabId="settings-page" tabType="settings">
                  <ProjectSettingsPage />
                </TabErrorBoundary>
              )}
            </>
          ) : activeTab ? (
            // Show other tab content when not on a list page
            <TabErrorBoundary tabId={activeTab.id} tabType={activeTab.type}>
              <TabContent tab={activeTab} project={project} onTabOpen={handleTabOpen} onOpenMolecule={handleOpenMolecule} />
            </TabErrorBoundary>
          ) : (
            // Fallback when no tab is active
            <div className="flex items-center justify-center h-full text-gray-500">
              <p>Click "Home" in the sidebar to get started</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

// Main component that provides the tab context
export function ProjectTabLayout() {
  const { projectId } = useParams<{ projectId: string }>();
  
  if (!projectId) {
    return <div>No project ID provided</div>;
  }
  
  return (
    <TabProvider projectId={projectId}>
      <ProjectTabLayoutInner />
    </TabProvider>
  );
}
